import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { LoginService } from "./login.service";
import { environment } from "../../../../environments/environment";
import { PkceService } from "./pkce.service";
import { AUTH_CONSTANTS } from '../../shared/constants/auth.constants';

@Injectable()
export class AuthGuardService  {
	tokenExp: number;

	constructor(private loginService: LoginService
		, private router: Router
		, private pkceService: PkceService) {
			this.loginService = loginService;
			this.router = router;
			this.pkceService = pkceService;
		}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
		console.log('AuthGuardService.canActivate called for route:', state.url);
		
		const hasOAuthCallback = window.location.search && 
			(window.location.search.includes('code=') || window.location.search.includes('access_token=') || window.location.search.includes('error='));
		
		if (hasOAuthCallback) {
			console.log('OAuth callback detected, redirecting to access_token route');
			this.router.navigate(['/access_token'], { 
				queryParamsHandling: 'preserve',
				fragment: window.location.search.substring(1)
			});
			return false;
		}
		
		if (state.url && state.url !== '/error' && !state.url.includes('error') && state.url !== '/access_token') {
			localStorage['redirectURL'] = state.url;
		}
		
		try {
			const isLoggedIn = this.loginService.checkLogin();
			if (isLoggedIn) {
				return true;
			}
		} catch (error) {
			console.error('Error during login check:', error);
		}
		if (window.location.href.includes('access_token') || window.location.href.includes('code=')) {
			console.log('Already in OAuth flow, not redirecting again');
			return false;
		}

		this.redirectToOAuthWithPkce();
		return false;
	}

	private async redirectToOAuthWithPkce(): Promise<void> {
		// PKCE requires the code_verifier to be read from localStorage on the same
		// origin that performs the token exchange (the redirect_uri origin). When the
		// app is opened from a different origin (e.g. the internal GCP Cloud Run URL),
		// the verifier would be generated here but lost after the provider redirects to
		// redirect_uri, causing AADSTS50148 (code_verifier missing) on /token. Move to
		// the canonical origin first so verifier generation and redemption share it.
		const canonicalRedirect = AuthGuardService.resolveCanonicalRedirect(globalThis.location.origin, globalThis.location.hash);
		if (canonicalRedirect) {
			globalThis.location.href = canonicalRedirect;
			return;
		}

		try {
			const codeVerifier = this.pkceService.generateCodeVerifier();
			const codeChallenge = await this.pkceService.generateCodeChallenge(codeVerifier);
			
			this.pkceService.storePkceValues(codeVerifier, codeChallenge);
			
			const authUrl = new URL(environment.auth.url + AUTH_CONSTANTS.URL_PATH_AUTHORIZE);
			authUrl.searchParams.append('client_id', environment.auth.client_id);
			authUrl.searchParams.append('scope', environment.auth.scope);
			authUrl.searchParams.append('response_type', AUTH_CONSTANTS.RESPONSE_TYPE);
			authUrl.searchParams.append('redirect_uri', environment.auth.redirect_uri);
			authUrl.searchParams.append('response_mode', AUTH_CONSTANTS.RESPONSE_MODE);
			authUrl.searchParams.append('code_challenge', codeChallenge);
			authUrl.searchParams.append('code_challenge_method', AUTH_CONSTANTS.CODE_CHALLENGE_METHOD);
			authUrl.searchParams.append('prompt', 'select_account');
			
			window.location.href = authUrl.toString();

		} catch (error) {
			console.error('Error generating PKCE values:', error);
			const authUrl = new URL(environment.auth.url + AUTH_CONSTANTS.URL_PATH_AUTHORIZE);
			authUrl.searchParams.append('client_id', environment.auth.client_id);
			authUrl.searchParams.append('scope', environment.auth.scope);
			authUrl.searchParams.append('response_type', AUTH_CONSTANTS.RESPONSE_TYPE);
			authUrl.searchParams.append('redirect_uri', environment.auth.redirect_uri);
			authUrl.searchParams.append('response_mode', AUTH_CONSTANTS.RESPONSE_MODE);
			authUrl.searchParams.append('prompt', 'select_account');
			
			window.location.href = authUrl.toString();
		}
	}

	/** Returns the URL to move to the canonical redirect_uri origin, preserving the
	 * current hash route, or null when already on the canonical origin. Keeping PKCE
	 * generation and token redemption on the same origin avoids AADSTS50148.
	 */
	static resolveCanonicalRedirect(currentOrigin: string, hash: string): string | null {
		const canonicalOrigin = new URL(environment.auth.redirect_uri).origin;
		if (currentOrigin === canonicalOrigin) {
			return null;
		}
		return canonicalOrigin + '/' + hash;
	}
}
